import React, { useEffect, useRef, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { Download, Image, FileSpreadsheet } from 'lucide-react'
import { useTheme } from '../../utils/theme'
import { downloadChartAsExcel, downloadChartAsImage } from '../../utils/downloadChart'

const BarChart = ({
  data,
  title,
  color,
  activeIndex,
  myIndex,
  height = '450px',
  canDownload = true,
  indicatorId,
  countryCode,
}) => {
  const chartRef = useRef(null)
  const downloadRef = useRef(null)
  const { colors } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const formatLabel = (label) => {
    const maxLength = 18
    return label.length > maxLength
      ? label.slice(0, maxLength) + '...'
      : label
  }

  const datasets = data.series.map((s) => ({
    label: s.name,
    data: s.values,
    backgroundColor: `${s.color}${colors.chartFillAlpha}`,
    hoverBackgroundColor: `${s.color}80`,
    borderColor: s.color,
    borderWidth: 2,
    borderRadius: 4,
    borderSkipped: false,
  }))

  const chartData = {
    labels: data.labels,
    datasets
  }

  const showLegend = data.series.length > 1

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 700,
    },

    layout: {
      padding: {
        bottom: 20,
        left: 10,
        right: 10,
        top: 10
      }
    },

    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom',
        labels: {
          useBorderRadius: true,
          borderRadius: 4,
          color: colors.textSecondary,
        }
      },
      tooltip: {
        backgroundColor: colors.cardBackground,
        titleColor: colors.textPrimary,
        bodyColor: colors.textSecondary,
        borderColor: colors.border,
        borderWidth: 1,
        callbacks: {
          title: (ctx) => {
            // full label on hover
            return data.labels[ctx[0].dataIndex]
          },
          label: (ctx) => {
            return `Value: ${ctx.formattedValue}`
          }
        }
      }
    },

    scales: {
      x: {
        grid: {
          color: colors.border,
        },
        ticks: {
          color: colors.textSecondary,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value) {
            const label = this.getLabelForValue(value)

            if (!label) return ''

            const maxLength = 18
            return label.length > maxLength
              ? label.slice(0, maxLength) + '...'
              : label
          }
        }
      },
      y: {
        grid: {
          color: colors.border,
        },
        ticks: {
          color: colors.textSecondary,
        }
      }
    },
  }

  useEffect(() => {
    if (activeIndex !== myIndex) return

    const chart = chartRef.current
    if (!chart) return

    const t = setTimeout(() => {
      chart.reset()
      chart.update()
    }, 50)

    return () => clearTimeout(t)
  }, [activeIndex, myIndex])

  const handleImageDownload = () => {
    setMenuOpen(false)
    downloadChartAsImage(chartRef.current, title, `${title || 'chart'}.png`)
  }

  const handleExcelDownload = async () => {
    setMenuOpen(false)
    setExporting(true)
    try {
      await downloadChartAsExcel(countryCode, indicatorId, `${title || 'chart'}.xlsx`)
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    if (!menuOpen) return

    const handleClickOutside = (event) => {
      if (
        downloadRef.current &&
        !downloadRef.current.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  return (
    <div className="chart-wrapper" style={{ height, width: '100%' }}>
      <div className="chart-header-row">
        <div className="chart-title">{title}</div>

        {/* aria-disabled, not the native `disabled` attribute: a truly
            disabled <button> is excluded from hit-testing in some
            browsers at a level CSS can't reliably override. aria-disabled
            keeps it a normal element — the click handler just no-ops —
            which also means the tooltip is fully React-state-driven
            below rather than depending on CSS :hover timing. */}
        <div
          ref={downloadRef}
          className="chart-download"
          onMouseEnter={() => !canDownload && setTooltipVisible(true)}
          onMouseLeave={() => setTooltipVisible(false)}
        >
          <button
            type="button"
            className="chart-download-trigger"
            aria-disabled={!canDownload || exporting}
            onClick={() => {
              if (!canDownload || exporting) {
                // Instant on click/tap — also covers touch devices, which
                // never get a hover state in the first place.
                setTooltipVisible(true)
                return
              }
              setMenuOpen((prev) => !prev)
            }}
          >
            <Download size={15} />
          </button>

          {!canDownload && tooltipVisible && (
            <span className="chart-download-tooltip" role="tooltip">
              Premium feature
            </span>
          )}

          {menuOpen && canDownload && (
            <div className="chart-download-menu">
              <button type="button" onClick={handleImageDownload}>
                <Image size={14} />
                Image (PNG)
              </button>
              <button type="button" onClick={handleExcelDownload} disabled={exporting}>
                <FileSpreadsheet size={14} />
                {exporting ? 'Exporting…' : 'Excel (XLSX)'}
              </button>
            </div>
          )}
        </div>
      </div>

      <Bar ref={chartRef} data={chartData} options={options} />
    </div>
  )
}

export default BarChart
